import {
  Box,
  Form,
  Card,
  Divider,
  FormLayout,
  InlineGrid,
  Select,
  Text,
  TextField,
  BlockStack,
  useBreakpoints,
  Checkbox,
  ContextualSaveBar,
} from '@shopify/polaris';

import { useCallback, useEffect, useState } from 'react';
import helpers from 'helpers';

import { lengthLessThan, lengthMoreThan, useField, useForm } from '@shopify/react-form';

import { TypedUser } from 'interface/user.model';
import DateTimeInput from 'components/dateTimeInput';
import __ from 'languages/index';
import { useNotification } from 'context/NotificationContext';
import { useUpdateCustomer, useSetCustomerRole } from 'queries/customer.query';
import { useUserRoleContext } from 'context/UserRoleContext';
import { useAuth } from 'context/AuthContext';

/**
 *   Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

export default function CustomerEditInformation({ onClose, entity }: { onClose: Function; entity?: TypedUser }) {
  const { currentUserCan } = useAuth();

  const { addNotification } = useNotification();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateCustomer();
  const { mutateAsync: changeCustomerRole, isPending } = useSetCustomerRole();

  const onChangeCustomerRoleCallback = useCallback(
    async (new_role: string) => {
      try {
        await changeCustomerRole({
          customer_role: new_role,
          customer_id: entity?.user_id,
        });
      } catch (e) {}
    },
    [entity]
  );

  /** Lấy user role ra ... */
  const { customerRoleSelection: allRoles } = useUserRoleContext();

  /** Hết code lấy user role ra ... */

  /** end user group */

  const { smUp } = useBreakpoints();

  const useFields = {
    user_login: useField<string>({
      value: entity?.user_login || '',
      validates: [
        (inputValue) => {
          if (inputValue && !/^[a-zA-Z]/.test(inputValue)) {
            return 'Tên đăng nhập phải bắt đầu bằng một chữ cái (A–Z hoặc a–z).';
          }
        },
        lengthLessThan(60, 'Không được dài hơn 60 ký tự.'),
        lengthMoreThan(4, 'Không được ngắn hơn 4 ký tự.'),
        (inputValue) => {
          if (inputValue && inputValue.length > 1) {
            if (helpers.isUTF8(inputValue)) {
              return 'Không được dùng mã Unicode trong trường này!';
            }
            if (/^[a-zA-Z0-9_.]+$/.exec(inputValue) === null) {
              return 'Chỉ sử dụng chữ cái, số và dấu chấm';
            }
          }
        },
      ],
    }),
    user_nickname: useField<string>({
      value: entity?.user_nickname || '',
      validates: [lengthLessThan(100, 'Không được dài hơn 100 ký tự.')],
    }),

    referrer_code: useField<string>({
      value: entity?.referrer_code || '',
      validates: [
        (inputValue) => {
          if (inputValue && inputValue.length > 0) {
            if (helpers.isUTF8(inputValue)) {
              return 'Không được dùng mã Unicode trong trường này!';
            }
            if (/^[a-zA-Z0-9_.]+$/.exec(inputValue) === null) {
              return 'Chỉ sử dụng chữ cái, số và dấu gạch dưới.';
            }
            if (inputValue.length > 12) {
              return 'Không được dài hơn 12 ký tự.';
            }
          }
        },
      ],
    }),

    user_email: useField<string>({
      value: entity?.user_email || undefined,
      validates: [
        (inputValue) => {
          if (inputValue && !helpers.isEmail(inputValue)) {
            return 'Your email is not valid!';
          }
        },
      ],
    }),

    display_name: useField<string>({
      value: entity?.display_name || undefined,
      validates: [lengthLessThan(60, 'Tên của bạn quá dài!'), lengthMoreThan(2, 'Tên của bạn quá ngắn!')],
    }),

    bio: useField<string>({
      value: entity?.bio || '',
      validates: [lengthLessThan(250, 'Mô tả quá dài!')],
    }),

    user_address: useField<string>({
      value: entity?.user_address || '',
      validates: [lengthLessThan(250, 'Mô tả quá dài!')],
    }),

    user_job: useField<string>({
      value: '',
      validates: [],
    }),

    user_title: useField<string>({
      value: entity?.user_title || '',
      validates: [],
    }),

    user_birthday: useField<string>({
      value: entity?.user_birthday || '',
      validates: [],
    }),

    user_phonenumber: useField<string>({
      value: entity?.user_phonenumber || undefined,
      validates: [
        (inputValue) => {
          if (inputValue) {
            if (!new RegExp('^[0-9]+$', 'g').test(inputValue)) {
              return 'Định dạng số điện thoại không hợp lệ. Chỉ dùng số và dấu cách!';
            }
          }
        },
      ],
    }),

    user_status: useField<number>({
      value: entity?.user_status || 0,
      validates: [],
    }),
  };

  const {
    fields,
    submit,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateEntity({
          user_id: entity?.user_id,
          referrer_code: values.referrer_code || undefined,
          user_email: values.user_email,
          user_status: values.user_status,
          user_login: values.user_login || undefined,
          user_nickname: values.user_nickname || undefined,
          display_name: values.display_name,
          bio: values.bio,
          user_address: values.user_address,
          user_phonenumber: values.user_phonenumber,
          user_job: values.user_job,
          user_title: values.user_title,
          user_birthday: values.user_birthday || null,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  useEffect(() => {
    if (updateSuccess) {
      // resetForm();
      onClose();
      addNotification('info', __('update_successfully'));
    }
  }, [updateSuccess]);

  return (
    <>
      <Form onSubmit={submit}>
        <BlockStack gap={{ xs: '800', sm: '400' }}>
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Tùy chọn
                </Text>
                <Text as="p" variant="bodyMd">
                  Các tùy chọn tài khoản.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <FormLayout>
                  <Checkbox
                    label="Cho phép khách đăng nhập"
                    helpText="Nếu tắt, khách hàng này không thể đăng nhập nữa."
                    checked={useFields.user_status.value === 1}
                    onChange={(e) => {
                      useFields.user_status.onChange(Number(e));
                    }}
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}
          {currentUserCan(['view_affiliates']) ? (
            <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
              <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
                <BlockStack gap="400">
                  <Text as="h3" variant="headingMd">
                    Vai trò
                  </Text>
                </BlockStack>
              </Box>
              <Card roundedAbove="sm">
                <BlockStack gap="400">
                  <Select
                    label="Vai trò"
                    disabled={isPending}
                    options={allRoles}
                    onChange={(a) => onChangeCustomerRoleCallback(a)}
                    value={String(entity?.user_role)}
                  />
                  <Text as="p" variant="bodyMd">
                    Khách hàng có nhiều vai trò khác nhau, khi chọn một vai trò mới, người dùng sẽ nhận được thông báo.
                  </Text>
                </BlockStack>
              </Card>
            </InlineGrid>
          ) : null}

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Thông tin chung
                </Text>
                <Text as="p" variant="bodyMd">
                  Càng chi tiết càng tốt.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <FormLayout>
                <FormLayout.Group condensed>
                  <TextField
                    autoFocus
                    autoComplete="off"
                    label="Username"
                    helpText={<p>user_login không được phép trùng lặp với bất kỳ một tài khoản nào!</p>}
                    {...fields.user_login}
                  />
                  <TextField autoComplete="off" label="Biệt danh" {...fields.user_nickname} />
                </FormLayout.Group>
                <TextField
                  autoComplete="off"
                  label="Mã giới thiệu"
                  maxLength={12}
                  helpText={<p>Không nên đổi mã nhiều lần, dẫn tới mã cũ bị vô hiệu. Mã này phải độc nhất.</p>}
                  {...fields.referrer_code}
                />

                <TextField
                  label="Tên hiển thị"
                  autoComplete="off"
                  error={fields.display_name.error}
                  value={fields.display_name.value}
                  onChange={(e) => fields.display_name.onChange(helpers.capitalizeWords(e))}
                  connectedLeft={
                    <Select
                      value={useFields.user_title.value}
                      options={[
                        { value: '', label: 'Không set' },
                        { value: 'mr', label: 'Anh' },
                        { value: 'miss', label: 'Cô' },
                        { value: 'mrs', label: 'Chị' },

                        { value: 'madam', label: 'Quý bà' },
                        { value: 'sir', label: 'Quý ông' },
                      ]}
                      onChange={(e) => useFields.user_title.onChange(e)}
                      label={''}
                    />
                  }
                />

                <DateTimeInput
                  onDateChange={(dateObjectOrnull) => {
                    if (!dateObjectOrnull) {
                      fields.user_birthday.onChange(``);
                    } else {
                      let { day, month, year } = dateObjectOrnull;
                      fields.user_birthday.onChange(`${year}-${month}-${day}`);
                    }
                  }}
                  label={'Sinh nhật'}
                  initialDate={fields.user_birthday.value}
                  error={fields.user_birthday.error}
                />

                <TextField
                  maxLength={250}
                  max={250}
                  showCharacterCount={true}
                  autoComplete="off"
                  label="Giới thiệu ngắn"
                  {...fields.bio}
                  multiline={3}
                />
              </FormLayout>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Thông tin liên hệ
                </Text>
                <Text as="p" variant="bodyMd">
                  Càng nhiều thông tin càng tốt
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Số điện thoại"
                      autoComplete="off"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      clearButton
                      onClearButtonClick={() => {
                        fields.user_phonenumber.onChange('');
                      }}
                      {...fields.user_phonenumber}
                    />
                    <TextField
                      label="Email"
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => {
                        fields.user_email.onChange('');
                      }}
                      {...fields.user_email}
                    />
                  </FormLayout.Group>

                  <TextField
                    label="Địa chỉ"
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => {
                      fields.user_address.onChange('');
                    }}
                    {...fields.user_address}
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </InlineGrid>
        </BlockStack>
      </Form>

      {dirty && (
        <ContextualSaveBar
          message="Thay đổi chưa được lưu"
          saveAction={{
            content: __('update_label_button'),
            onAction: () => submit(),
            loading: updating,
          }}
          discardAction={{
            content: 'Thu hồi',
            onAction: () => resetForm(),
          }}
        />
      )}

      <br />
      <br />
      <br />
      <br />
      <br />
    </>
  );
}

